/**
 * 961 Glow storefront runtime.
 *
 * One ES module (loaded with type="module" by snippets/glow-foundation.liquid)
 * that implements the data-attribute API described in BUILD-SPEC.md section 4:
 * drawers, cart AJAX + Section Rendering refresh, product forms, quick add,
 * cart lines, variant picker, steppers, announcement rotation, predictive
 * search, load more, filters, share and the product gallery.
 *
 * Vanilla, no dependencies. Every selector may be absent; every binding is
 * idempotent so GlowUI.init(root) can be re-run on replaced markup.
 * Only two globals are exposed: window.GlowCart and window.GlowUI.
 */

/* -------------------------------------------------------------------------
 * 0. Config: read <script type="application/json" id="glow-settings"> with
 *    safe defaults for every key so the runtime works even when the snippet
 *    is missing or partially filled.
 * ---------------------------------------------------------------------- */
const ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const DEFAULTS = {
  freeDeliveryThreshold: 50,
  deliveryFee: 5,
  lowStockThreshold: 3,
  whatsapp: '',
  routes: {
    cart: `${ROOT_URL}cart`,
    cartAdd: `${ROOT_URL}cart/add`,
    cartChange: `${ROOT_URL}cart/change`,
    search: `${ROOT_URL}search`,
    predictive: `${ROOT_URL}search/suggest`,
  },
  strings: {
    added: 'Added',
    quickAdded: 'Added to your bag',
    soldOut: 'Sold out',
    copied: 'Link copied',
    error: 'Something went wrong. Try again.',
  },
};

/** Merge `over` onto `base`, ignoring null / undefined / empty-string values. */
function mergeDefined(base, over) {
  const out = { ...base };
  Object.keys(over || {}).forEach((key) => {
    const value = over[key];
    if (value !== null && value !== undefined && value !== '') out[key] = value;
  });
  return out;
}

/** Shopify's AJAX endpoints are the `.js` variants of the cart routes. */
function withJs(url) {
  return /\.js(\?|$)/.test(url) ? url : `${url}.js`;
}

function readConfig() {
  let parsed = {};
  const el = document.getElementById('glow-settings');
  if (el) {
    try {
      parsed = JSON.parse(el.textContent || '{}') || {};
    } catch (err) {
      console.warn('[glow] #glow-settings is not valid JSON', err);
    }
  }
  const cfg = mergeDefined(DEFAULTS, parsed);
  cfg.routes = mergeDefined(DEFAULTS.routes, parsed.routes);
  cfg.strings = mergeDefined(DEFAULTS.strings, parsed.strings);
  cfg.freeDeliveryThreshold = Number(cfg.freeDeliveryThreshold) || 0;
  cfg.deliveryFee = Number(cfg.deliveryFee) || 0;
  cfg.lowStockThreshold = Number(cfg.lowStockThreshold) || 0;
  cfg.routes.cartAdd = withJs(cfg.routes.cartAdd);
  cfg.routes.cartChange = withJs(cfg.routes.cartChange);
  cfg.routes.cartJson = withJs(cfg.routes.cart);
  return cfg;
}

let config = readConfig();

/* -------------------------------------------------------------------------
 * 1. Small DOM helpers.
 * ---------------------------------------------------------------------- */
const bound = new WeakSet(); // elements that already received per-element bindings

function $$(selector, root = document) {
  const list = Array.from(root.querySelectorAll(selector));
  if (root instanceof Element && root.matches(selector)) list.unshift(root);
  return list;
}

function cssEscape(value) {
  return window.CSS && typeof CSS.escape === 'function' ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function parseHTML(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function sectionIdOf(el) {
  const section = el && el.closest('.shopify-section');
  return section && section.id ? section.id.replace(/^shopify-section-/, '') : '';
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root) {
  return $$(FOCUSABLE, root).filter((el) => el.getClientRects().length > 0);
}

/** Polite live region so add-to-cart / copy feedback is announced to screen readers. */
let liveRegion = null;
function announce(text) {
  if (!text || !document.body) return;
  if (!liveRegion || !liveRegion.isConnected) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'glow-live';
    liveRegion.className = 'glow-sr';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    document.body.appendChild(liveRegion);
  }
  liveRegion.textContent = '';
  window.requestAnimationFrame(() => {
    liveRegion.textContent = text;
  });
}

/* Button labels: prefer a <span data-glow-label>, else the first non-empty text
 * node, so icons inside the button survive a label swap. Icon-only buttons
 * (no text) keep their markup and only get aria-label / live-region feedback. */
function labelNode(btn) {
  const span = btn.querySelector('[data-glow-label]');
  if (span) return span;
  for (const node of btn.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) return node;
  }
  return null;
}

function getLabel(btn) {
  const node = labelNode(btn);
  return node ? node.textContent.trim() : '';
}

function setLabel(btn, text) {
  const node = labelNode(btn);
  if (node) node.textContent = text;
}

const flashes = new WeakMap();
/** Temporarily swap a button label (and aria-label when present), then restore it. */
function flashLabel(btn, text, ms) {
  if (!btn) return;
  const previous = flashes.get(btn);
  const original = previous ? previous.original : { label: getLabel(btn), aria: btn.getAttribute('aria-label') };
  if (previous) clearTimeout(previous.timer);
  setLabel(btn, text);
  if (original.aria !== null) btn.setAttribute('aria-label', text);
  const timer = setTimeout(() => {
    setLabel(btn, original.label);
    if (original.aria !== null) btn.setAttribute('aria-label', original.aria);
    flashes.delete(btn);
  }, ms);
  flashes.set(btn, { original, timer });
}

/* -------------------------------------------------------------------------
 * 2. Drawers: native <dialog> elements. Open with [data-glow-open="id"],
 *    close with [data-glow-close], backdrop click or Esc. Adds
 *    body.glow-drawer-open, focuses the first focusable element and emits
 *    glow:drawer:open / glow:drawer:close.
 * ---------------------------------------------------------------------- */
const openers = new WeakMap(); // dialog -> element that opened it

function drawerDuration(dialog) {
  if (reducedMotion()) return 0;
  const raw = getComputedStyle(dialog).getPropertyValue('--dur-drawer').trim();
  if (!raw) return 320;
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return 320;
  return /ms$/.test(raw) ? n : n * 1000;
}

function bindDialog(dialog) {
  if (!dialog || bound.has(dialog) || typeof dialog.showModal !== 'function') return;
  bound.add(dialog);
  // Backdrop click: the event target is the dialog itself, not its content.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeDrawer(dialog);
  });
  // Esc: run the animated close instead of the instant native one.
  dialog.addEventListener('cancel', (e) => {
    if (drawerDuration(dialog) > 0) {
      e.preventDefault();
      closeDrawer(dialog);
    }
  });
  dialog.addEventListener('close', () => afterClose(dialog));
}

function initDialogs(root) {
  $$('dialog.glow-drawer, dialog[data-glow-drawer]', root).forEach(bindDialog);
}

function focusFirst(dialog) {
  const preferred = dialog.querySelector('[data-glow-autofocus], [autofocus]');
  const target = preferred || focusables(dialog)[0];
  if (target) target.focus({ preventScroll: true });
}

function openDrawer(target, opener) {
  const dialog = typeof target === 'string' ? document.getElementById(target) : target;
  if (!dialog || typeof dialog.showModal !== 'function') return false;
  if (dialog.open) return true;
  bindDialog(dialog);
  dialog.classList.remove('is-closing');
  openers.set(dialog, opener || document.activeElement);
  dialog.showModal();
  document.body.classList.add('glow-drawer-open');
  focusFirst(dialog);
  // A header search input that opens the search sheet hands its text over.
  if (opener instanceof HTMLInputElement && opener.value) {
    const input = dialog.querySelector('[data-glow-search-input]');
    if (input) {
      input.value = opener.value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
  dialog.dispatchEvent(new CustomEvent('glow:drawer:open', { bubbles: true, detail: { id: dialog.id, opener } }));
  return true;
}

function closeDrawer(target) {
  const dialog = typeof target === 'string' ? document.getElementById(target) : target;
  if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
  const ms = drawerDuration(dialog);
  if (ms <= 0) {
    dialog.close();
    return;
  }
  // `.is-closing` lets glow.css run the reverse slide before the dialog closes.
  dialog.classList.add('is-closing');
  let timer = 0;
  const done = () => {
    clearTimeout(timer);
    dialog.removeEventListener('transitionend', onEnd);
    dialog.classList.remove('is-closing');
    if (dialog.open) dialog.close();
  };
  const onEnd = (e) => {
    if (e.target === dialog) done();
  };
  dialog.addEventListener('transitionend', onEnd);
  timer = setTimeout(done, ms + 50); // fallback when the CSS transition never fires
}

function afterClose(dialog) {
  dialog.classList.remove('is-closing');
  if (!document.querySelector('dialog.glow-drawer[open], dialog[data-glow-drawer][open]')) {
    document.body.classList.remove('glow-drawer-open');
  }
  const opener = openers.get(dialog);
  openers.delete(dialog);
  if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
  dialog.dispatchEvent(new CustomEvent('glow:drawer:close', { bubbles: true, detail: { id: dialog.id } }));
}

/* -------------------------------------------------------------------------
 * 3. Cart API (window.GlowCart). Mutations are serialised through a promise
 *    chain so responses always apply in order. Each mutation asks Shopify to
 *    render the cart sections in the same request (`sections` param); when a
 *    response has no sections we fall back to GET routes.cart?sections=...
 * ---------------------------------------------------------------------- */
let chain = Promise.resolve();
function enqueue(task) {
  const run = chain.then(task, task);
  chain = run.catch(() => {});
  return run;
}

/** Nearest [data-glow-cart-error] region: inside the form, then its dialog,
 *  then its section, else the first one on the page (usually the drawer's). */
function errorTarget(context) {
  let node = context instanceof Element ? context : null;
  while (node) {
    const holder = node.closest('form, dialog, .shopify-section');
    if (!holder) break;
    const el = holder.querySelector('[data-glow-cart-error]');
    if (el) return el;
    node = holder.parentElement;
  }
  return document.querySelector('#glow-cart-drawer [data-glow-cart-error]') || document.querySelector('[data-glow-cart-error]');
}

function clearError(context) {
  const el = errorTarget(context);
  if (el) {
    el.textContent = '';
    el.hidden = true;
  }
}

/** Surface a cart error in [data-glow-cart-error] when present, else console.warn. */
function fail(context, message, cause) {
  const el = errorTarget(context);
  if (el) {
    el.textContent = message;
    el.hidden = false;
    // A quick-add error lands in the drawer's region: bring that drawer into view.
    const dialog = el.closest('dialog');
    if (dialog && !dialog.open) openDrawer(dialog, context instanceof HTMLElement ? context : undefined);
  } else {
    console.warn(`[glow] ${message}`, cause);
  }
  announce(message);
  const err = new Error(message);
  err.cause = cause;
  return err;
}

async function request(url, init, context) {
  let res;
  try {
    res = await fetch(url, { credentials: 'same-origin', ...init });
  } catch (err) {
    throw fail(context, config.strings.error, err);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errors = data.errors && typeof data.errors === 'object' ? Object.values(data.errors).flat().join(' ') : '';
    throw fail(context, data.description || errors || data.message || `${config.strings.error} (${res.status})`, data);
  }
  return data;
}

/** Find the section wrapper for a logical id, including keyed group sections
 *  whose DOM id is `shopify-section-sections--123__glow-header`. */
function findSectionEl(id) {
  return (
    document.getElementById(`shopify-section-${id}`) ||
    document.querySelector(`[id^="shopify-section-"][id$="__${cssEscape(id)}"]`)
  );
}

/** ids = glow-cart-drawer, glow-header, every [data-glow-cart-section] value, plus extras. */
function sectionTargets(extra) {
  const wanted = ['glow-cart-drawer', 'glow-header'];
  $$('[data-glow-cart-section]').forEach((el) => {
    const value = el.getAttribute('data-glow-cart-section');
    if (value) wanted.push(value.trim());
  });
  const extras = Array.isArray(extra) ? extra : String(extra || '').split(',');
  extras.forEach((value) => value && wanted.push(String(value).trim()));

  const targets = [];
  const seen = new Set();
  wanted.forEach((id) => {
    const el = findSectionEl(id);
    if (!el) return;
    const requestId = el.id.replace(/^shopify-section-/, '');
    if (seen.has(requestId)) return;
    seen.add(requestId);
    targets.push({ id, requestId, el });
  });
  return targets;
}

async function fetchSections(targets) {
  if (!targets.length) return {};
  const ids = targets.map((t) => t.requestId).join(',');
  // No Accept: application/json here: with it Shopify answers /cart with the cart JSON
  // instead of the rendered sections object.
  const res = await fetch(`${config.routes.cart}?sections=${ids}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Section render failed (${res.status})`);
  return res.json();
}

async function fetchCartJson() {
  const res = await fetch(config.routes.cartJson, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
  return res.json();
}

/* Focus memory: replacing the drawer's inner HTML would drop keyboard focus
 * to <body>. Remember what was focused and put focus back on its twin. */
function rememberFocus(root) {
  const active = document.activeElement;
  if (!(active instanceof Element) || !root.contains(active)) return null;
  const line = active.closest('[data-glow-line]');
  const memo = {
    key: line ? line.getAttribute('data-glow-line') : null,
    step: active.getAttribute('data-glow-step'),
    qty: active.matches('[data-glow-qty]'),
    remove: active.matches('[data-glow-remove]'),
    close: active.matches('[data-glow-close]'),
    id: active.id || '',
    name: active.getAttribute('name') || '',
    field: null,
  };
  // Typed text survives the swap (a discount code, the header search);
  // cart quantities are not kept because the server value is the truth.
  if (active.matches('input, textarea, select') && !memo.qty) {
    let start = null;
    let end = null;
    try {
      start = active.selectionStart;
      end = active.selectionEnd;
    } catch (err) {
      /* selection is not supported on every input type */
    }
    memo.field = { value: active.value, start, end };
  }
  return memo;
}

function restoreFocus(root, memo) {
  if (!memo) return;
  let target = null;
  if (memo.key) {
    const line = root.querySelector(`[data-glow-line="${cssEscape(memo.key)}"]`);
    if (line) {
      if (memo.step) target = line.querySelector(`[data-glow-step="${cssEscape(memo.step)}"]:not([disabled])`);
      else if (memo.qty) target = line.querySelector('[data-glow-qty]');
      else if (memo.remove) target = line.querySelector('[data-glow-remove]');
      if (!target) target = line.querySelector('[data-glow-qty], button, a[href]');
    }
  }
  if (!target && memo.id) target = root.querySelector(`[id="${cssEscape(memo.id)}"]`);
  if (!target && memo.name) target = root.querySelector(`[name="${cssEscape(memo.name)}"]`);
  if (target && memo.field && target.matches('input, textarea, select')) {
    target.value = memo.field.value;
    if (memo.field.start !== null && typeof target.setSelectionRange === 'function') {
      try {
        target.setSelectionRange(memo.field.start, memo.field.end);
      } catch (err) {
        /* number / email inputs reject setSelectionRange */
      }
    }
  }
  if (!target) target = root.querySelector('[data-glow-autofocus], [data-glow-close]') || focusables(root)[0];
  if (target) target.focus({ preventScroll: true });
}

/** Swap one section's markup. The drawer keeps its <dialog>: only
 *  .glow-drawer__inner is replaced so an open drawer stays open. */
function replaceSection(el, fresh) {
  const memo = rememberFocus(el);
  // Only a section that IS a drawer (its first element is the <dialog>, like the cart
  // drawer) gets the inner swap. The header holds the menu and search dialogs too, and
  // matching those would rewrite the wrong markup.
  const ownDialog = (node) => {
    const first = node.firstElementChild;
    return first && first.tagName === 'DIALOG' ? first : null;
  };
  const currentDialog = ownDialog(el);
  const freshDialog = ownDialog(fresh);
  const currentInner = currentDialog && currentDialog.querySelector('.glow-drawer__inner');
  const freshInner = freshDialog && freshDialog.querySelector('.glow-drawer__inner');
  let root = el;
  if (currentInner && freshInner) {
    currentInner.innerHTML = freshInner.innerHTML;
    root = currentInner;
  } else if (currentDialog && freshDialog && currentDialog.open) {
    // No __inner wrapper: keep the open <dialog> element and swap what is inside it.
    currentDialog.innerHTML = freshDialog.innerHTML;
    root = currentDialog;
  } else if (el.querySelector('dialog[open]')) {
    // Never wipe a section that holds another open dialog (the menu and search
    // sheets live inside the header); updateCounts() patches the bubble anyway.
    return;
  } else {
    el.innerHTML = fresh.innerHTML;
  }
  GlowUI.init(root);
  restoreFocus(root, memo);
  el.dispatchEvent(
    new CustomEvent('glow:section:replaced', { bubbles: true, detail: { id: el.id.replace(/^shopify-section-/, ''), root } })
  );
}

function applySections(targets, html) {
  if (!html || typeof html !== 'object') return;
  targets.forEach(({ id, requestId, el }) => {
    const markup = typeof html[requestId] === 'string' ? html[requestId] : html[id];
    if (typeof markup !== 'string' || !el.isConnected) return;
    const doc = parseHTML(markup);
    const fresh = doc.getElementById(el.id) || doc.querySelector('.shopify-section') || doc.body;
    replaceSection(el, fresh);
  });
}

function bump(el) {
  el.classList.remove('is-bump');
  void el.offsetWidth; // restart the animation when the class is re-added quickly
  el.classList.add('is-bump');
  setTimeout(() => el.classList.remove('is-bump'), 300);
}

/** The item count currently shown (or last known), used to decide on a bump. */
function readCount() {
  const el = document.querySelector('[data-glow-cart-count]');
  const shown = el ? parseInt(el.textContent, 10) : NaN;
  if (Number.isFinite(shown)) return shown;
  if (GlowCart.cart && typeof GlowCart.cart.item_count === 'number') return GlowCart.cart.item_count;
  // No bubble rendered at all means the header hid it for an empty cart.
  return el ? null : 0;
}

/** Update every [data-glow-cart-count]: text = item_count, hidden when 0, bump on change.
 *  `force` bumps even when the markup already shows the new number (a replaced header). */
function updateCounts(cart, root = document, force = false) {
  if (!cart || typeof cart.item_count !== 'number') return;
  const count = String(cart.item_count);
  const empty = cart.item_count === 0;
  $$('[data-glow-cart-count]', root).forEach((el) => {
    const changed = el.textContent.trim() !== count;
    el.textContent = count;
    el.hidden = empty;
    el.classList.toggle('is-empty', empty);
    // The hidden attribute loses to any `display` rule on the badge class.
    if (empty) el.style.display = 'none';
    else el.style.removeProperty('display');
    if ((changed || force) && !empty) bump(el);
  });
}

/** After a mutation: get sections + cart JSON (in parallel when needed), apply, notify. */
async function settle(targets, sectionsHtml, cart) {
  const before = readCount();
  const soft = (promise) =>
    promise.catch((err) => {
      console.warn('[glow] cart refresh failed', err);
      return null;
    });
  const [html, cartJson] = await Promise.all([
    soft(sectionsHtml ? Promise.resolve(sectionsHtml) : fetchSections(targets)),
    soft(cart ? Promise.resolve(cart) : fetchCartJson()),
  ]);
  if (html) applySections(targets, html);
  if (cartJson) {
    GlowCart.cart = cartJson;
    updateCounts(cartJson, document, before !== null && before !== cartJson.item_count);
  }
  document.dispatchEvent(new CustomEvent('glow:cart:updated', { detail: cartJson || GlowCart.cart || null }));
  return cartJson;
}

const JSON_HEADERS = { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

async function doAdd(payload, opts = {}) {
  const targets = sectionTargets(opts.sections);
  const ids = targets.map((t) => t.requestId).join(',');
  let data;
  if (payload instanceof FormData) {
    if (ids) {
      payload.set('sections', ids);
      payload.set('sections_url', window.location.pathname);
    }
    data = await request(
      config.routes.cartAdd,
      { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: payload },
      opts.context
    );
  } else {
    let body = payload;
    if (typeof payload === 'string' || typeof payload === 'number') body = { id: payload, quantity: 1 };
    body = { ...(body || {}), sections: ids, sections_url: window.location.pathname };
    data = await request(config.routes.cartAdd, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }, opts.context);
  }
  clearError(opts.context);
  await settle(targets, data.sections, null); // add.js returns the line, not the cart
  // add() opens the drawer unless the caller opts out (data-glow-no-open / {open:false}).
  if (opts.open !== false) {
    const opener = opts.opener || (opts.context instanceof HTMLElement ? opts.context : undefined);
    openDrawer('glow-cart-drawer', opener);
  }
  return data;
}

async function doChange(key, quantity, opts = {}) {
  const targets = sectionTargets(opts.sections);
  const body = {
    id: key,
    quantity: Math.max(0, parseInt(quantity, 10) || 0),
    sections: targets.map((t) => t.requestId).join(','),
    sections_url: window.location.pathname,
  };
  const data = await request(
    config.routes.cartChange,
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) },
    opts.context
  );
  clearError(opts.context);
  const { sections, ...cart } = data; // change.js returns the whole cart
  await settle(targets, sections, cart);
  return cart;
}

const GlowCart = {
  /** Last cart JSON seen (null until the first mutation or get()). */
  cart: null,
  /** payload: FormData | {id, quantity, properties} | {items:[...]} | variant id.
   *  opts: { context, opener, open (default true), sections } */
  add(payload, opts) {
    return enqueue(() => doAdd(payload, opts));
  },
  change(key, quantity, opts) {
    return enqueue(() => doChange(key, quantity, opts));
  },
  remove(key, opts) {
    return GlowCart.change(key, 0, opts);
  },
  /** Re-render the cart sections (plus any extra section ids) from the current cart. */
  refresh(extraSectionIds) {
    return enqueue(() => settle(sectionTargets(extraSectionIds), null, null));
  },
  get() {
    return fetchCartJson().then((cart) => {
      GlowCart.cart = cart;
      return cart;
    });
  },
};

/* -------------------------------------------------------------------------
 * 4. Steppers and cart lines. `.glow-stepper [data-glow-step]` adjusts its
 *    input (min 1, max from data-max / max). Inside [data-glow-line="key"]
 *    a quantity change is debounced and sent through GlowCart.change.
 * ---------------------------------------------------------------------- */
function bounds(input) {
  const rawMin = parseInt(input.getAttribute('data-min') || input.min, 10);
  const rawMax = parseInt(input.getAttribute('data-max') || input.max, 10);
  const min = Number.isFinite(rawMin) && rawMin >= 0 ? rawMin : 1;
  const max = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : 999;
  return { min, max: Math.max(min, max) };
}

function syncStepper(stepper, input) {
  if (!stepper || !input) return;
  const { min, max } = bounds(input);
  const value = parseInt(input.value, 10) || min;
  $$('[data-glow-step]', stepper).forEach((btn) => {
    const dir = parseInt(btn.getAttribute('data-glow-step'), 10) || 0;
    const off = (dir < 0 && value <= min) || (dir > 0 && value >= max);
    if (off && document.activeElement === btn) input.focus({ preventScroll: true });
    btn.disabled = off;
  });
}

function initSteppers(root) {
  $$('.glow-stepper', root).forEach((stepper) => {
    const input = stepper.querySelector('input[data-glow-qty], input');
    if (input) syncStepper(stepper, input);
  });
}

function handleStep(btn) {
  const dir = parseInt(btn.getAttribute('data-glow-step'), 10) || 0;
  const stepper = btn.closest('.glow-stepper') || btn.parentElement;
  const input = stepper && (stepper.querySelector('input[data-glow-qty]') || stepper.querySelector('input'));
  if (!input || !dir) return;
  const { min, max } = bounds(input);
  const current = parseInt(input.value, 10) || min;
  const next = clamp(current + dir, min, max);
  if (next === current) {
    syncStepper(stepper, input);
    return;
  }
  input.value = String(next);
  syncStepper(stepper, input);
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

const lineTimers = new Map(); // line key -> debounce timer
const linePending = new Set(); // line keys with a change request in flight

function handleQtyChange(input) {
  const { min, max } = bounds(input);
  const qty = clamp(parseInt(input.value, 10) || min, min, max);
  input.value = String(qty);
  const stepper = input.closest('.glow-stepper');
  if (stepper) syncStepper(stepper, input);

  const line = input.closest('[data-glow-line]');
  const key = line && line.getAttribute('data-glow-line');
  if (!line || !key) return; // product-form stepper: nothing to send

  clearTimeout(lineTimers.get(key));
  lineTimers.delete(key);
  // Back to the server-rendered quantity with nothing in flight: nothing to send.
  // (With a request pending the server may already hold another value, so send.)
  if (!linePending.has(key) && qty === parseInt(input.defaultValue, 10)) {
    line.classList.remove('is-busy');
    return;
  }
  line.classList.add('is-busy');
  lineTimers.set(
    key,
    setTimeout(() => {
      lineTimers.delete(key);
      linePending.add(key);
      GlowCart.change(key, qty, { context: line })
        .catch(() => {})
        .finally(() => {
          linePending.delete(key);
          line.classList.remove('is-busy');
        });
    }, 300)
  );
}

function handleRemove(btn) {
  const line = btn.closest('[data-glow-line]');
  const key = btn.getAttribute('data-glow-remove') || (line && line.getAttribute('data-glow-line'));
  if (!key) return;
  clearTimeout(lineTimers.get(key));
  lineTimers.delete(key);
  if (line) line.classList.add('is-busy');
  btn.setAttribute('aria-busy', 'true');
  GlowCart.remove(key, { context: btn })
    .catch(() => {})
    .finally(() => {
      if (line) line.classList.remove('is-busy');
      btn.removeAttribute('aria-busy');
    });
}

/* -------------------------------------------------------------------------
 * 5. Product form and quick add.
 * ---------------------------------------------------------------------- */
async function submitProductForm(form, submitter) {
  if (form.classList.contains('is-busy')) return;
  const btn =
    (submitter instanceof Element && submitter.matches('[data-glow-add]') && submitter) ||
    form.querySelector('[data-glow-add]') ||
    (form.id && document.querySelector(`[data-glow-add][form="${cssEscape(form.id)}"]`)) ||
    null;
  if (btn && (btn.disabled || btn.getAttribute('aria-disabled') === 'true')) return;

  form.classList.add('is-busy');
  if (btn) {
    btn.classList.add('is-busy');
    btn.setAttribute('aria-busy', 'true');
  }
  clearError(form);
  try {
    await GlowCart.add(new FormData(form), {
      context: form,
      opener: btn || form,
      open: !form.hasAttribute('data-glow-no-open'),
    });
    if (btn) flashLabel(btn, config.strings.added, 1200);
    announce(config.strings.added);
    form.dispatchEvent(new CustomEvent('glow:product:added', { bubbles: true, detail: { cart: GlowCart.cart } }));
  } catch (err) {
    // Already surfaced by fail(); keep the form usable.
  } finally {
    form.classList.remove('is-busy');
    if (btn) {
      btn.classList.remove('is-busy');
      btn.removeAttribute('aria-busy');
    }
  }
}

async function handleQuickAdd(btn) {
  if (btn.classList.contains('is-busy')) return;
  const id = btn.getAttribute('data-glow-quick-add');
  if (!id) return;
  btn.classList.add('is-busy');
  btn.setAttribute('aria-busy', 'true');
  try {
    await GlowCart.add({ id, quantity: 1 }, { context: btn, opener: btn, open: !btn.hasAttribute('data-glow-no-open') });
    btn.classList.add('is-added');
    announce(config.strings.quickAdded);
    setTimeout(() => btn.classList.remove('is-added'), 1400);
  } catch (err) {
    // Surfaced by fail().
  } finally {
    btn.classList.remove('is-busy');
    btn.removeAttribute('aria-busy');
  }
}

/* -------------------------------------------------------------------------
 * 6. Variant picker. Root [data-glow-variant-picker] holds a JSON script of
 *    variants and radios / selects named options[...]. On change the variant
 *    is resolved by option values and price, stock line, ATC button, restock
 *    panel, quantity max, gallery and URL are updated.
 * ---------------------------------------------------------------------- */
const pickerData = new WeakMap(); // picker -> variants array

function initPickers(root) {
  $$('[data-glow-variant-picker]', root).forEach((picker) => {
    if (bound.has(picker)) return;
    bound.add(picker);
    let variants = [];
    const script = picker.querySelector('script[data-glow-variants]');
    try {
      variants = JSON.parse((script && script.textContent) || '[]');
    } catch (err) {
      console.warn('[glow] data-glow-variants JSON invalid', err);
    }
    pickerData.set(picker, Array.isArray(variants) ? variants : []);
  });
}

/** Selected value per option group, ordered by data-glow-option-index (any
 *  consistent numbering, e.g. option.position) or by DOM order. */
function optionGroups(picker) {
  const groups = new Map();
  $$('input[name^="options["], select[name^="options["]', picker).forEach((ctrl) => {
    if (!groups.has(ctrl.name)) {
      const holder = ctrl.closest('[data-glow-option-index]');
      const raw = ctrl.getAttribute('data-glow-option-index') || (holder && holder.getAttribute('data-glow-option-index'));
      const index = parseInt(raw, 10);
      groups.set(ctrl.name, { name: ctrl.name, index: Number.isFinite(index) ? index : groups.size, value: null, ctrl });
    }
    const group = groups.get(ctrl.name);
    if (ctrl instanceof HTMLSelectElement) group.value = ctrl.value;
    else if (ctrl.checked) group.value = ctrl.value;
  });
  return Array.from(groups.values()).sort((a, b) => a.index - b.index);
}

function variantOptions(variant) {
  if (Array.isArray(variant.options)) return variant.options.map(String);
  return [variant.option1, variant.option2, variant.option3].filter((v) => v !== null && v !== undefined).map(String);
}

function resolveVariant(variants, groups) {
  const values = groups.map((g) => (g.value === null ? '' : String(g.value)));
  const exact = variants.find((v) => {
    const opts = variantOptions(v);
    return opts.length === values.length && opts.every((o, i) => o === values[i]);
  });
  if (exact) return exact;
  // Fallback when the DOM order does not match option positions.
  return (
    variants.find((v) => {
      const opts = variantOptions(v);
      return opts.length === values.length && values.every((val) => opts.includes(val));
    }) || null
  );
}

/** Replace [target] with the content of <template ...="{id}">. When the template
 *  holds a single element carrying the same marker attribute, the element is
 *  swapped wholesale (so classes like .is-sale / .glow-stock--out update). */
function swapFromTemplate(scope, marker, templateSelector) {
  const tpl = scope.querySelector(templateSelector) || document.querySelector(templateSelector);
  if (!(tpl instanceof HTMLTemplateElement)) return;
  $$(`[${marker}]`, scope).forEach((target) => {
    const frag = tpl.content.cloneNode(true);
    const only = frag.children.length === 1 ? frag.firstElementChild : null;
    if (only && only.hasAttribute(marker)) target.replaceWith(only);
    else target.replaceChildren(frag);
  });
}

/** ATC button state: 'available' | 'sold-out' | 'unavailable' (no variant matches the
 *  chosen options: disabled, label untouched). The original label is kept in
 *  data-label; Liquid may set it explicitly for buttons rendered sold out. */
function setAddButton(btn, state) {
  if (!btn.hasAttribute('data-label')) {
    const current = getLabel(btn);
    if (current && current !== config.strings.soldOut) btn.setAttribute('data-label', current);
  }
  const original = btn.getAttribute('data-label');
  btn.disabled = state !== 'available';
  if (state === 'sold-out') setLabel(btn, config.strings.soldOut);
  else if (original) setLabel(btn, original);
}

function applyVariant(picker, variant) {
  const scope = picker.closest('.shopify-section, [data-glow-product]') || document;
  const available = !!(variant && variant.available);
  const soldOut = !!variant && !available;
  const id = variant ? String(variant.id) : '';

  if (id) {
    $$('form[data-glow-product-form] input[name="id"]', scope).forEach((input) => {
      input.value = id;
    });
    swapFromTemplate(scope, 'data-glow-price', `template[data-glow-variant-price="${cssEscape(id)}"]`);
    swapFromTemplate(scope, 'data-glow-stock', `template[data-glow-variant-stock="${cssEscape(id)}"]`);
  }

  const state = available ? 'available' : soldOut ? 'sold-out' : 'unavailable';
  $$('[data-glow-add]', scope).forEach((btn) => setAddButton(btn, state));
  // The restock panel replaces the add bar only for a real, sold-out variant.
  $$('[data-glow-restock]', scope).forEach((el) => {
    el.hidden = !soldOut;
  });
  $$('[data-glow-atc-bar]', scope).forEach((el) => {
    el.hidden = soldOut;
  });

  // Quantity max follows tracked inventory (unless overselling is allowed).
  const tracked = !!(
    variant &&
    variant.inventory_management &&
    variant.inventory_policy !== 'continue' &&
    Number(variant.inventory_quantity) > 0
  );
  $$('form[data-glow-product-form] input[data-glow-qty]', scope).forEach((input) => {
    if (tracked) input.setAttribute('data-max', String(variant.inventory_quantity));
    else input.removeAttribute('data-max');
    const { min, max } = bounds(input);
    input.value = String(clamp(parseInt(input.value, 10) || min, min, max));
    syncStepper(input.closest('.glow-stepper'), input);
  });

  // Gallery: jump to the variant's media when a thumb declares data-media-id.
  const mediaId =
    variant &&
    (variant.featured_media_id ||
      (variant.featured_media && variant.featured_media.id) ||
      (variant.featured_image && variant.featured_image.id));
  if (mediaId) {
    const thumb = scope.querySelector(`[data-glow-thumb][data-media-id="${cssEscape(String(mediaId))}"]`);
    if (thumb) activateThumb(thumb);
  }

  if (id && window.history && typeof history.replaceState === 'function') {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    history.replaceState(history.state, '', url.toString());
  }
  picker.dispatchEvent(new CustomEvent('glow:variant:change', { bubbles: true, detail: { variant, available } }));
}

function handleVariantChange(picker) {
  if (!pickerData.has(picker)) initPickers(picker);
  const groups = optionGroups(picker);
  // "Label caps + selected value": mirror the chosen value next to the option name.
  groups.forEach((group) => {
    const holder = group.ctrl.closest('fieldset, [data-glow-option]');
    const out = holder && holder.querySelector('[data-glow-option-value]');
    if (out && group.value !== null) out.textContent = group.value;
  });
  applyVariant(picker, resolveVariant(pickerData.get(picker) || [], groups));
}

/* -------------------------------------------------------------------------
 * 7. Announcement bar: rotate .glow-announce__msg every data-interval ms
 *    (values under 100 are read as seconds), pause on hover / focus / hidden
 *    tab, static when interval is 0, one message, or reduced motion.
 * ---------------------------------------------------------------------- */
const announceState = new WeakMap(); // bar -> { show, msgs, pause }

function initAnnouncements(root) {
  $$('[data-glow-announce]', root).forEach((bar) => {
    if (bound.has(bar)) return;
    bound.add(bar);
    const msgs = $$('.glow-announce__msg', bar);
    if (!msgs.length) return;
    let index = Math.max(0, msgs.findIndex((m) => m.classList.contains('is-active')));
    const show = (n) => {
      index = ((n % msgs.length) + msgs.length) % msgs.length;
      msgs.forEach((m, k) => {
        const on = k === index;
        m.classList.toggle('is-active', on);
        m.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
    };
    show(index);

    let interval = parseInt(bar.getAttribute('data-interval'), 10) || 0;
    if (interval > 0 && interval < 100) interval *= 1000;
    let paused = false;
    announceState.set(bar, {
      show,
      msgs,
      pause: (p) => {
        paused = p;
      },
    });
    if (msgs.length < 2 || interval <= 0 || reducedMotion()) return;

    const timer = window.setInterval(() => {
      if (!bar.isConnected) {
        window.clearInterval(timer);
        return;
      }
      if (!paused && !document.hidden) show(index + 1);
    }, interval);
    bar.addEventListener('mouseenter', () => {
      paused = true;
    });
    bar.addEventListener('mouseleave', () => {
      paused = false;
    });
    bar.addEventListener('focusin', () => {
      paused = true;
    });
    bar.addEventListener('focusout', () => {
      paused = false;
    });
  });
}

/* -------------------------------------------------------------------------
 * 8. Predictive search: 250ms debounce, AbortController for stale requests,
 *    results = innerHTML of the returned #shopify-section-glow-predictive-search.
 * ---------------------------------------------------------------------- */
const searchState = new WeakMap(); // form -> { timer, controller }

function searchPart(form, selector) {
  return form.querySelector(selector) || (form.closest('dialog, .shopify-section') || document).querySelector(selector);
}

function abortSearch(form) {
  const st = searchState.get(form);
  if (!st) return;
  clearTimeout(st.timer);
  if (st.controller) st.controller.abort();
  st.controller = null;
  form.removeAttribute('aria-busy');
}

function renderSearch(form, html) {
  const results = searchPart(form, '[data-glow-search-results]');
  const idle = searchPart(form, '[data-glow-search-idle]');
  if (results) {
    results.innerHTML = html;
    results.hidden = !html;
    if (html) GlowUI.init(results);
  }
  if (idle) idle.hidden = !!html;
}

async function runSearch(form, query) {
  abortSearch(form);
  const st = searchState.get(form);
  const controller = new AbortController();
  st.controller = controller;
  form.setAttribute('aria-busy', 'true');
  const url =
    `${config.routes.predictive}?q=${encodeURIComponent(query)}` +
    '&resources[type]=product,query&resources[limit]=5&resources[limit_scope]=each&section_id=glow-predictive-search';
  try {
    const res = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`Predictive search failed (${res.status})`);
    const doc = parseHTML(await res.text());
    const section = doc.getElementById('shopify-section-glow-predictive-search');
    renderSearch(form, section ? section.innerHTML : doc.body.innerHTML);
  } catch (err) {
    if (err && err.name !== 'AbortError') console.warn('[glow] predictive search failed', err);
  } finally {
    if (st.controller === controller) {
      st.controller = null;
      form.removeAttribute('aria-busy');
    }
  }
}

function handleSearchInput(input) {
  const form = input.closest('[data-glow-search-form]') || input.form;
  if (!form) return;
  const st = searchState.get(form) || { timer: 0, controller: null };
  searchState.set(form, st);
  clearTimeout(st.timer);
  const query = input.value.trim();
  if (!query) {
    abortSearch(form);
    renderSearch(form, '');
    return;
  }
  st.timer = setTimeout(() => runSearch(form, query), 250);
}

function initSearch(root) {
  $$('input[data-glow-search-input]', root).forEach((input) => {
    if (bound.has(input)) return;
    bound.add(input);
    input.setAttribute('autocomplete', 'off');
  });
}

/* -------------------------------------------------------------------------
 * 9. Load more: fetch the link's href as a section render, append the new
 *    .glow-grid children, refresh "Showing n of N" and swap / drop the button.
 * ---------------------------------------------------------------------- */
async function handleLoadMore(link) {
  if (link.classList.contains('is-busy') || !link.href) return;
  const section = link.closest('.shopify-section');
  const scope = section || document;
  const grid = scope.querySelector('.glow-grid');
  if (!grid) return;
  const url = new URL(link.href, window.location.href);
  const sectionId = sectionIdOf(link);
  if (sectionId) url.searchParams.set('section_id', sectionId);

  link.classList.add('is-busy');
  link.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`Load more failed (${res.status})`);
    const doc = parseHTML(await res.text());
    const freshGrid = doc.querySelector('.glow-grid');
    const items = freshGrid ? Array.from(freshGrid.children) : [];
    items.forEach((item) => grid.appendChild(item));

    // Both counters (desktop total, mobile "Showing n of N") update in document order.
    const showings = Array.from(scope.querySelectorAll('[data-glow-showing]'));
    const freshShowings = Array.from(doc.querySelectorAll('[data-glow-showing]'));
    showings.forEach((node, i) => {
      if (freshShowings[i]) node.textContent = freshShowings[i].textContent;
    });

    const next = doc.querySelector('a[data-glow-load-more]');
    if (next) link.replaceWith(next);
    else link.remove();

    GlowUI.init(grid);
    const first = items[0] && items[0].querySelector('a[href], button');
    if (first) first.focus({ preventScroll: true });
  } catch (err) {
    console.warn('[glow] load more failed', err);
    link.classList.remove('is-busy');
    link.removeAttribute('aria-busy');
  }
}

/* -------------------------------------------------------------------------
 * 10. Filters: a form[data-glow-filters] submits itself on change. Inside a
 *     dialog (mobile) or with data-glow-filters="manual" it waits for the
 *     "Show n results" button and only refreshes that button's count.
 * ---------------------------------------------------------------------- */
function submitForm(form) {
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.submit();
}

const filterState = new WeakMap(); // form -> { timer, controller }

function refreshFilterCount(form, btn) {
  const sectionId = sectionIdOf(form);
  if (!sectionId) return;
  const st = filterState.get(form) || { timer: 0, controller: null };
  filterState.set(form, st);
  clearTimeout(st.timer);
  st.timer = setTimeout(async () => {
    if (st.controller) st.controller.abort();
    const controller = new AbortController();
    st.controller = controller;
    const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.href);
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.searchParams.set('section_id', sectionId);
    try {
      const res = await fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!res.ok) return;
      const fresh = parseHTML(await res.text()).querySelector('[data-glow-filters-submit]');
      if (fresh && btn.isConnected) setLabel(btn, fresh.textContent.trim());
    } catch (err) {
      if (err && err.name !== 'AbortError') console.warn('[glow] filter count failed', err);
    }
  }, 300);
}

function handleFilterChange(form) {
  const manual = form.getAttribute('data-glow-filters') === 'manual' || !!form.closest('dialog');
  if (!manual) {
    submitForm(form);
    return;
  }
  const btn = form.querySelector('[data-glow-filters-submit]');
  if (btn) refreshFilterCount(form, btn);
}

function handleClearFilters(el, e) {
  const href = el.getAttribute('href') || el.getAttribute('data-href');
  if (!href) return;
  e.preventDefault();
  window.location.assign(href);
}

/* -------------------------------------------------------------------------
 * 11. Share: Web Share API, else copy the URL and flash strings.copied.
 * ---------------------------------------------------------------------- */
function legacyCopy(text) {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.className = 'glow-sr';
  document.body.appendChild(area);
  area.select();
  try {
    document.execCommand('copy');
  } catch (err) {
    console.warn('[glow] copy failed', err);
  }
  area.remove();
}

async function handleShare(btn) {
  const url = btn.getAttribute('data-url') || btn.getAttribute('data-glow-share') || window.location.href;
  const title = btn.getAttribute('data-title') || document.title;
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      return;
    } catch (err) {
      if (err && err.name === 'AbortError') return; // user dismissed the sheet
    }
  }
  try {
    await navigator.clipboard.writeText(url);
  } catch (err) {
    legacyCopy(url);
  }
  // Text buttons swap their label; the icon-only 44px button gets .is-copied for CSS.
  flashLabel(btn, config.strings.copied, 1500);
  btn.classList.add('is-copied');
  setTimeout(() => btn.classList.remove('is-copied'), 1500);
  announce(config.strings.copied);
}

/* -------------------------------------------------------------------------
 * 12. Gallery: [data-glow-thumb] (data-src / data-srcset / data-alt) swaps
 *     the [data-glow-gallery-main] image; arrow keys move between thumbs.
 * ---------------------------------------------------------------------- */
function activateThumb(thumb) {
  const gallery = thumb.closest('[data-glow-gallery]');
  if (!gallery) return;
  const main = gallery.querySelector('[data-glow-gallery-main]');
  const img = main && (main.matches('img') ? main : main.querySelector('img'));
  if (img) {
    const srcset = thumb.getAttribute('data-srcset');
    const sizes = thumb.getAttribute('data-sizes');
    const src = thumb.getAttribute('data-src');
    const alt = thumb.getAttribute('data-alt');
    if (srcset) img.srcset = srcset;
    else img.removeAttribute('srcset');
    if (sizes) img.sizes = sizes;
    if (src) img.src = src;
    if (alt !== null) img.alt = alt;
  }
  $$('[data-glow-thumb]', gallery).forEach((t) => {
    const on = t === thumb;
    t.classList.toggle('is-active', on);
    if (on) t.setAttribute('aria-current', 'true');
    else t.removeAttribute('aria-current');
  });
}

function initGalleries(root) {
  $$('[data-glow-gallery]', root).forEach((gallery) => {
    if (bound.has(gallery)) return;
    bound.add(gallery);
    const active = gallery.querySelector('[data-glow-thumb].is-active');
    if (active) active.setAttribute('aria-current', 'true');
  });
}

function handleThumbKey(thumb, e) {
  const rtl = document.documentElement.dir === 'rtl';
  const dirs = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1, ArrowDown: 1, ArrowUp: -1 };
  const dir = dirs[e.key];
  if (!dir) return;
  const gallery = thumb.closest('[data-glow-gallery]');
  const thumbs = gallery ? $$('[data-glow-thumb]', gallery) : [];
  const i = thumbs.indexOf(thumb);
  if (i < 0 || thumbs.length < 2) return;
  e.preventDefault();
  const next = thumbs[(i + dir + thumbs.length) % thumbs.length];
  next.focus();
  activateThumb(next);
}

/* -------------------------------------------------------------------------
 * 13. Delegated document listeners (bound once). Delegation keeps every
 *     click / submit / change binding valid after sections are replaced.
 * ---------------------------------------------------------------------- */
function plainClick(e) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

function onClick(e) {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;

  const opener = t.closest('[data-glow-open]');
  if (opener) {
    if (!plainClick(e)) return;
    if (openDrawer(opener.getAttribute('data-glow-open'), opener)) e.preventDefault();
    return;
  }
  const closer = t.closest('[data-glow-close]');
  if (closer) {
    const dialog = closer.closest('dialog') || document.getElementById(closer.getAttribute('data-glow-close') || '');
    if (dialog) {
      e.preventDefault();
      closeDrawer(dialog);
    }
    return;
  }
  const step = t.closest('[data-glow-step]');
  if (step) {
    e.preventDefault();
    handleStep(step);
    return;
  }
  const remove = t.closest('[data-glow-remove]');
  if (remove) {
    e.preventDefault();
    handleRemove(remove);
    return;
  }
  const quick = t.closest('[data-glow-quick-add]');
  if (quick) {
    e.preventDefault();
    handleQuickAdd(quick);
    return;
  }
  const share = t.closest('[data-glow-share]');
  if (share) {
    e.preventDefault();
    handleShare(share);
    return;
  }
  const thumb = t.closest('[data-glow-thumb]');
  if (thumb) {
    e.preventDefault();
    activateThumb(thumb);
    return;
  }
  const more = t.closest('a[data-glow-load-more]');
  if (more) {
    if (!plainClick(e)) return;
    e.preventDefault();
    handleLoadMore(more);
    return;
  }
  const clear = t.closest('[data-glow-clear-filters]');
  if (clear && plainClick(e)) handleClearFilters(clear, e);
}

function onSubmit(e) {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.matches('[data-glow-product-form]')) {
    e.preventDefault();
    submitProductForm(form, e.submitter);
    return;
  }
  if (form.matches('[data-glow-search-form]')) abortSearch(form); // full search page takes over
}

function onChange(e) {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (t.matches('input[data-glow-qty]')) {
    handleQtyChange(t);
    return;
  }
  const picker = t.closest('[data-glow-variant-picker]');
  if (picker && t.matches('input[name^="options["], select[name^="options["]')) {
    handleVariantChange(picker);
    return;
  }
  const filters = t.closest('form[data-glow-filters]');
  if (filters) handleFilterChange(filters);
}

function onInput(e) {
  const t = e.target;
  if (t instanceof HTMLInputElement && t.matches('[data-glow-search-input]')) handleSearchInput(t);
}

function onKeydown(e) {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const thumb = t.closest('[data-glow-thumb]');
  if (thumb) {
    handleThumbKey(thumb, e);
    return;
  }
  // Enter in a cart line quantity sends the change instead of submitting the whole cart form.
  if (e.key === 'Enter' && t instanceof HTMLInputElement && t.matches('[data-glow-qty]') && t.closest('[data-glow-line]')) {
    e.preventDefault();
    handleQtyChange(t);
  }
}

/* Theme editor: re-bind loaded sections and preview the selected announcement. */
function onEditorSectionLoad(e) {
  if (e.target instanceof Element) GlowUI.init(e.target);
}

/* Theme editor: selecting the cart drawer section shows the drawer. */
function onEditorSectionSelect(e) {
  const target = e.target instanceof Element ? e.target : null;
  const dialog = target && target.querySelector('dialog#glow-cart-drawer');
  if (!dialog) return;
  if (e.type === 'shopify:section:select') openDrawer(dialog);
  else closeDrawer(dialog);
}

/* Back/forward cache: the cart may have changed on the page we came from. */
function onPageShow(e) {
  if (e.persisted) GlowCart.refresh().catch(() => {});
}

function onEditorBlockSelect(e) {
  const target = e.target instanceof Element ? e.target : null;
  const bar = target && target.closest('[data-glow-announce]');
  const state = bar && announceState.get(bar);
  if (!state) return;
  const msg = target.closest('.glow-announce__msg') || target.querySelector('.glow-announce__msg');
  const index = msg ? state.msgs.indexOf(msg) : -1;
  state.pause(e.type === 'shopify:block:select');
  if (index >= 0) state.show(index);
}

let globalBound = false;
function bindGlobal() {
  if (globalBound) return;
  globalBound = true;
  document.addEventListener('click', onClick);
  document.addEventListener('submit', onSubmit);
  document.addEventListener('change', onChange);
  document.addEventListener('input', onInput);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('shopify:section:load', onEditorSectionLoad);
  document.addEventListener('shopify:section:select', onEditorSectionSelect);
  document.addEventListener('shopify:section:deselect', onEditorSectionSelect);
  document.addEventListener('shopify:block:select', onEditorBlockSelect);
  document.addEventListener('shopify:block:deselect', onEditorBlockSelect);
  window.addEventListener('pageshow', onPageShow);
}

/* -------------------------------------------------------------------------
 * 14. Public UI surface and boot.
 * ---------------------------------------------------------------------- */
const GlowUI = {
  get config() {
    return config;
  },
  /** Bind everything inside `root` (document by default). Safe to call repeatedly. */
  init(root = document) {
    const ok = root instanceof Element || root instanceof Document || root instanceof DocumentFragment;
    const scope = ok ? root : document;
    bindGlobal();
    initDialogs(scope);
    initAnnouncements(scope);
    initPickers(scope);
    initSteppers(scope);
    initSearch(scope);
    initGalleries(scope);
    if (GlowCart.cart) updateCounts(GlowCart.cart, scope);
    return scope;
  },
  openDrawer,
  closeDrawer,
  announce,
  flashLabel,
};

window.GlowCart = GlowCart;
window.GlowUI = GlowUI;

function boot() {
  config = readConfig(); // the settings JSON is guaranteed to be parsed by now
  GlowUI.init(document);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();

export { GlowCart, GlowUI };
